import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthCrmService, GrowthSegmentMemberResponse, GrowthSegmentResponse } from './growth-crm.service';
import {
  GrowthSegmentRuleConditionForm,
  GrowthSegmentRuleField,
  GrowthSegmentRuleForm,
  createDefaultSegmentRuleForm,
  createSegmentRuleCondition,
  formatSegmentMemberReason,
  serializeSegmentRuleForm,
  validateSegmentRuleForm
} from './growth-segment-rule.models';

@Component({
  standalone: true,
  selector: 'ain-growth-segments',
  imports: [
    AinPageComponent, AinStateComponent, AinFormFieldComponent, InfoHintComponent,
    FormsModule, TableModule, ButtonModule, DialogModule, InputTextModule, SelectModule, InputNumberModule,
    AinDatePipe
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.crm.segments.title')"
      [description]="t('growth.crm.segments.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.crm.segments.help.page.title'"
        [descriptionKey]="'growth.crm.segments.help.page.description'"
        [bulletKeys]="[
          'growth.crm.segments.help.page.bullets.rules',
          'growth.crm.segments.help.page.bullets.evaluate',
          'growth.crm.segments.help.page.bullets.partyRef'
        ]" />

      <div class="toolbar">
        <div class="toolbar-start">
          <button pButton type="button" severity="secondary" icon="pi pi-refresh" [label]="t('common.refresh')" (click)="load()"></button>
        </div>
        <div class="toolbar-end">
          <button pButton type="button" [label]="t('growth.crm.segments.create')" (click)="openCreateDialog()"></button>
        </div>
      </div>

      <section class="card">
        <div class="section-title">
          <h3>{{ t('growth.crm.segments.sections.list') }}</h3>
        </div>
        @if (loading()) {
          <ain-state type="loading" [title]="t('growth.crm.segments.loading')" />
        } @else if (error()) {
          <ain-state type="error" [title]="t('growth.crm.segments.errorTitle')" [message]="error() ?? ''" />
        } @else if (segments().length === 0) {
          <ain-state type="empty" [title]="t('growth.crm.segments.empty')" />
        } @else {
          <p-table [value]="segments()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.crm.segments.columns.key') }}</th>
                <th>{{ t('growth.crm.segments.columns.name') }}</th>
                <th>{{ t('growth.crm.segments.columns.status') }}</th>
                <th>{{ t('growth.crm.segments.columns.lastEvaluated') }}</th>
                <th>{{ t('growth.crm.segments.columns.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.key }}</td>
                <td>{{ row.name }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.lastEvaluatedAtUtc ? (row.lastEvaluatedAtUtc | ainDate:'datetime') : '-' }}</td>
                <td class="actions">
                  <button pButton type="button" size="small" [label]="t('growth.crm.segments.actions.evaluate')" (click)="evaluate(row)"></button>
                  <button pButton type="button" size="small" severity="secondary" [label]="t('growth.crm.segments.actions.members')" (click)="loadMembers(row)"></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      @if (selectedSegment()) {
        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.crm.segments.membersTitle') }} — {{ selectedSegment()!.name }}</h3>
            <ain-info-hint
              [titleKey]="'growth.crm.segments.help.members.title'"
              [descriptionKey]="'growth.crm.segments.help.members.description'"
              [bulletKeys]="['growth.crm.segments.help.members.bullets.partyRef']" />
          </div>
          @if (members().length === 0) {
            <ain-state type="empty" [title]="t('growth.crm.segments.membersEmpty')" />
          } @else {
            <p-table [value]="members()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.crm.segments.columns.party') }}</th>
                  <th>{{ t('growth.crm.segments.columns.reason') }}</th>
                  <th>{{ t('growth.crm.segments.columns.evaluatedAt') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ t('growth.crm.segments.partyRefLabel') }}</td>
                  <td>{{ formatMemberReason(row.reasonJson) }}</td>
                  <td>{{ row.evaluatedAtUtc | ainDate:'datetime' }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }

      <p-dialog
        [header]="t('growth.crm.segments.create')"
        [visible]="showCreateDialog()"
        (visibleChange)="showCreateDialog.set($event)"
        [modal]="true"
        [style]="{ width: 'min(760px, 96vw)' }">
        <div class="section-title">
          <h4>{{ t('growth.crm.segments.sections.create') }}</h4>
          <ain-info-hint
            [titleKey]="'growth.crm.segments.help.rules.title'"
            [descriptionKey]="'growth.crm.segments.help.rules.description'"
            [bulletKeys]="[
              'growth.crm.segments.help.rules.bullets.fields',
              'growth.crm.segments.help.rules.bullets.noCode',
              'growth.crm.segments.help.rules.bullets.deferredAceRefs'
            ]" />
        </div>

        <div class="dialog-grid">
          <ain-form-field [label]="t('growth.crm.segments.fields.key')">
            <input pInputText [(ngModel)]="createModel.key" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.crm.segments.fields.name')">
            <input pInputText [(ngModel)]="createModel.name" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.crm.segments.fields.status')" class="full-width">
            <p-select [options]="statusOptions" optionValue="value" [(ngModel)]="createModel.status" appendTo="body">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
            </p-select>
          </ain-form-field>
        </div>

        <section class="rule-card">
          <div class="section-title">
            <h4>{{ t('growth.crm.segments.sections.ruleBuilder') }}</h4>
          </div>
          <ain-form-field [label]="t('growth.crm.segments.fields.matchMode')">
            <p-select
              [options]="matchModeOptions"
              optionValue="value"
              [(ngModel)]="ruleForm.match"
              appendTo="body">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
            </p-select>
          </ain-form-field>

          <div class="rule-actions">
            <button pButton type="button" severity="secondary" [label]="t('growth.crm.segments.actions.addCondition')" (click)="addCondition()"></button>
            <button pButton type="button" severity="secondary" [text]="true" [label]="t('growth.crm.json.previewToggle')" (click)="showRuleJsonPreview.set(!showRuleJsonPreview())"></button>
          </div>

          @for (condition of ruleForm.conditions; track condition.localId) {
            <div class="condition-row">
              <ain-form-field [label]="t('growth.crm.segments.fields.ruleField')">
                <p-select
                  [options]="ruleFieldOptions"
                  optionValue="value"
                  [(ngModel)]="condition.field"
                  (ngModelChange)="onRuleFieldChanged(condition, $event)"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </ain-form-field>
              <ain-form-field [label]="t('growth.crm.segments.fields.ruleOperator')">
                <p-select
                  [options]="operatorOptionsFor(condition.field)"
                  optionValue="value"
                  [(ngModel)]="condition.op"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </ain-form-field>
              <ain-form-field [label]="t('growth.crm.segments.fields.ruleValue')">
                @if (isSelectValueField(condition.field)) {
                  <p-select
                    [options]="valueOptionsFor(condition.field)"
                    optionLabel="label"
                    optionValue="value"
                    [(ngModel)]="condition.value"
                    appendTo="body" />
                } @else if (condition.field === 'leadScoreMin') {
                  <p-inputNumber
                    [ngModel]="leadScoreValue(condition)"
                    (ngModelChange)="setLeadScoreValue(condition, $event)"
                    [min]="0"
                    [maxFractionDigits]="2" />
                } @else {
                  <input pInputText [(ngModel)]="condition.value" />
                }
              </ain-form-field>
              <div class="condition-actions">
                <button pButton type="button" severity="danger" [label]="t('common.remove')" (click)="removeCondition(condition.localId)"></button>
              </div>
            </div>
          }

          @if (ruleValidationError()) {
            <p class="validation-error">{{ t(ruleValidationError()!) }}</p>
          }

          @if (showRuleJsonPreview()) {
            <div class="json-preview">
              <span class="preview-label">{{ t('growth.crm.json.previewLabel') }}</span>
              <pre>{{ ruleJsonPreview() }}</pre>
            </div>
          }
        </section>

        <ng-template pTemplate="footer">
          <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="showCreateDialog.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="createSegment()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .toolbar-start, .toolbar-end, .actions, .rule-actions, .condition-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .rule-card { border: 1px solid #f3f4f6; border-radius: 10px; padding: 12px; display: grid; gap: 12px; margin-top: 12px; }
    .card h3, .card h4 { margin: 0; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .dialog-grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .full-width { grid-column: 1 / -1; }
    .condition-row { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); border-top: 1px dashed #e5e7eb; padding-top: 10px; }
    .condition-actions { display: flex; align-items: flex-end; }
    .validation-error { color: #b91c1c; margin: 0; }
    .json-preview { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; }
    .json-preview pre { margin: 6px 0 0; white-space: pre-wrap; word-break: break-word; font-size: 12px; }
    .preview-label { font-size: 12px; color: #6b7280; font-weight: 600; }
  `]
})
export class GrowthSegmentsComponent implements OnInit {
  private readonly crmService = inject(GrowthCrmService);
  private readonly localizationService = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly segments = signal<GrowthSegmentResponse[]>([]);
  readonly members = signal<GrowthSegmentMemberResponse[]>([]);
  readonly selectedSegment = signal<GrowthSegmentResponse | null>(null);
  readonly showCreateDialog = signal(false);
  readonly showRuleJsonPreview = signal(false);
  readonly ruleValidationError = signal<string | null>(null);
  readonly ruleJsonPreview = computed(() => serializeSegmentRuleForm(this.ruleForm));

  createModel = {
    key: '',
    name: '',
    status: 'Active'
  };

  ruleForm: GrowthSegmentRuleForm = createDefaultSegmentRuleForm();

  readonly statusOptions = [
    { value: 'Draft', labelKey: 'growth.crm.segments.status.draft' },
    { value: 'Active', labelKey: 'growth.crm.segments.status.active' },
    { value: 'Archived', labelKey: 'growth.crm.segments.status.archived' }
  ];

  readonly matchModeOptions = [
    { value: 'all', labelKey: 'growth.crm.segments.match.all' },
    { value: 'any', labelKey: 'growth.crm.segments.match.any' }
  ];

  readonly ruleFieldOptions: Array<{ value: GrowthSegmentRuleField; labelKey: string }> = [
    { value: 'lifecycleStage', labelKey: 'growth.crm.segments.ruleFields.lifecycleStage' },
    { value: 'tag', labelKey: 'growth.crm.segments.ruleFields.tag' },
    { value: 'leadStatus', labelKey: 'growth.crm.segments.ruleFields.leadStatus' },
    { value: 'leadScoreMin', labelKey: 'growth.crm.segments.ruleFields.leadScoreMin' },
    { value: 'activityType', labelKey: 'growth.crm.segments.ruleFields.activityType' },
    { value: 'channelKey', labelKey: 'growth.crm.segments.ruleFields.channelKey' },
    { value: 'industryKey', labelKey: 'growth.crm.segments.ruleFields.industryKey' }
  ];

  private readonly eqOperatorOptions = [{ value: 'eq', labelKey: 'growth.crm.segments.operators.eq' }];
  private readonly gteOperatorOptions = [{ value: 'gte', labelKey: 'growth.crm.segments.operators.gte' }];

  private readonly lifecycleStageValues = ['Prospect', 'Customer', 'Advocate'];
  private readonly leadStatusValues = ['New', 'Scored', 'Qualified', 'HandedOff', 'Disqualified'];
  private readonly activityTypeValues = ['content.viewed', 'form_submit', 'request_demo', 'request_quote', 'social_comment', 'social_dm'];
  private readonly channelValues = ['web', 'email', 'social', 'landing'];
  private readonly industryValues = ['retail', 'banking'];

  ngOnInit(): void {
    this.load();
  }

  openCreateDialog(): void {
    this.createModel = { key: '', name: '', status: 'Active' };
    this.ruleForm = createDefaultSegmentRuleForm();
    this.ruleValidationError.set(null);
    this.showRuleJsonPreview.set(false);
    this.showCreateDialog.set(true);
  }

  addCondition(): void {
    this.ruleForm.conditions = [...this.ruleForm.conditions, createSegmentRuleCondition()];
  }

  removeCondition(localId: string): void {
    if (this.ruleForm.conditions.length <= 1)
      return;
    this.ruleForm.conditions = this.ruleForm.conditions.filter(item => item.localId !== localId);
  }

  onRuleFieldChanged(condition: GrowthSegmentRuleConditionForm, field: GrowthSegmentRuleField): void {
    condition.field = field;
    condition.op = field === 'leadScoreMin' ? 'gte' : 'eq';
    condition.value = this.defaultValueForField(field);
  }

  operatorOptionsFor(field: GrowthSegmentRuleField): Array<{ value: string; labelKey: string }> {
    return field === 'leadScoreMin' ? this.gteOperatorOptions : this.eqOperatorOptions;
  }

  isSelectValueField(field: GrowthSegmentRuleField): boolean {
    return field !== 'tag' && field !== 'leadScoreMin';
  }

  valueOptionsFor(field: GrowthSegmentRuleField): Array<{ value: string; label: string }> {
    const values = this.valuesForField(field);
    return values.map(value => ({ value, label: value }));
  }

  leadScoreValue(condition: GrowthSegmentRuleConditionForm): number | null {
    const parsed = Number(condition.value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  setLeadScoreValue(condition: GrowthSegmentRuleConditionForm, value: number | null): void {
    condition.value = value == null ? '' : String(value);
  }

  formatMemberReason(reasonJson: string): string {
    return formatSegmentMemberReason(reasonJson);
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.crmService.listSegments().subscribe({
      next: items => {
        this.segments.set(items);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('growth.crm.segments.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  createSegment(): void {
    const validationError = validateSegmentRuleForm(this.ruleForm);
    if (validationError) {
      this.ruleValidationError.set(validationError);
      return;
    }

    if (!this.createModel.key.trim() || !this.createModel.name.trim()) {
      this.ruleValidationError.set('growth.crm.segments.ruleValidation.identityRequired');
      return;
    }

    this.ruleValidationError.set(null);
    this.saving.set(true);
    this.crmService.createSegment({
      key: this.createModel.key.trim(),
      name: this.createModel.name.trim(),
      ruleDefinitionJson: serializeSegmentRuleForm(this.ruleForm),
      evaluationMode: 'OnDemand',
      status: this.createModel.status
    }).subscribe({
      next: () => {
        this.showCreateDialog.set(false);
        this.saving.set(false);
        this.load();
      },
      error: () => {
        this.error.set(this.t('growth.crm.segments.errorCreate'));
        this.saving.set(false);
      }
    });
  }

  evaluate(segment: GrowthSegmentResponse): void {
    this.selectedSegment.set(segment);
    this.crmService.evaluateSegment(segment.id).subscribe({
      next: items => this.members.set(items),
      error: () => this.error.set(this.t('growth.crm.segments.errorEvaluate'))
    });
  }

  loadMembers(segment: GrowthSegmentResponse): void {
    this.selectedSegment.set(segment);
    this.crmService.listSegmentMembers(segment.id).subscribe({
      next: items => this.members.set(items),
      error: () => this.error.set(this.t('growth.crm.segments.errorMembers'))
    });
  }

  private defaultValueForField(field: GrowthSegmentRuleField): string {
    switch (field) {
      case 'lifecycleStage':
        return 'Prospect';
      case 'leadStatus':
        return 'New';
      case 'activityType':
        return 'form_submit';
      case 'channelKey':
        return 'web';
      case 'industryKey':
        return 'retail';
      case 'leadScoreMin':
        return '50';
      default:
        return '';
    }
  }

  private valuesForField(field: GrowthSegmentRuleField): string[] {
    switch (field) {
      case 'lifecycleStage':
        return this.lifecycleStageValues;
      case 'leadStatus':
        return this.leadStatusValues;
      case 'activityType':
        return this.activityTypeValues;
      case 'channelKey':
        return this.channelValues;
      case 'industryKey':
        return this.industryValues;
      default:
        return [];
    }
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
